import {Avatar, Modal} from 'antd'
import {course_getCourseClasses_Api} from 'apis'
import {getId} from 'apis/model/base'
import EmptyHolder, {renderAdminOnlyHolder} from 'components/EmptyHolder'
import getLinkToDetail from 'helpers/getLinkToDetail'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import {useHistory} from 'modules/navigation/useRouter'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {Link} from 'react-router-dom'
import {withProps} from 'recompose'
import EntityList from 'views/AppLocals/EntityList'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {ListHeaderWithLine} from 'views/AppLocals/Templates/items/ListHeader'
import {NullAsDefault, renderIf} from 'views/Shared'
import CRUD from 'views/Shared/enums/CRUD'
import {FetchClassById} from '../../components/FetchEntity'
import CourseContext from '../../CourseContext'
import {HeaderWithIconLine} from "../../../Templates/items/ListHeader";
import {GoDotFill} from "react-icons/go";
import {GiIronCross} from "react-icons/gi";
import {ClassroomSvg} from "../../../../../components/Svgs/ClassroomSvg";
import {LayoutContext} from "../../../../../components/layouts/Default/LayoutContext";
import {renderFromToTime} from "../../../../../helpers/datetimeHelpers";
import {BsInfoCircleFill} from "react-icons/bs";
import {ItemInformation} from "../../../Organization/OrganizationEntity";
import LoadingBlock from "../../../../LoadingBlock";

const renderItem =
    ({renderActions = NullAsDefault, handleAction = NullAsDefault} = {}) =>
        (item, index) => {
            const {course_class_idcode, description} = item ?? {}
            return (
                <Translate key={index}>
                    {(t) => (
                        <div
                            onClick={() => {handleAction(CRUD.READ, item)}}
                            className="p-2 flex flex-col hover:shadow-md rounded-xl relative">
                            <div className="flex items-center gap-2">
                                <GiIronCross size={12} className="text-primary-300"/>
                                <Link
                                    to={getLinkToDetail(item)}
                                    className="text-color-000 no-underline">
                                    <span className="font-bold text-base leading-snug">{getTitle(item)}</span>
                                </Link>
                            </div>
                            {renderIf(
                                description,
                                <div className="ml-5 mb-1 text-xs text-color-300 tracking-wider max-lines-2">
                                    {description}
                                </div>
                            )}
                            <div className="ml-5 flex items-center gap-1">
                                <GoDotFill size={10} className="text-color-400"/>
                                <span className="text-xs text-color-500 italic">
                                    {`${t('course class idcode')} : `}
                                </span>
                                <span className="font-medium text-xs text-blue-600 italic">
                                    {course_class_idcode}
                                </span>
                                <div className="flex-1"/>
                                {renderActions(item)}
                            </div>
                        </div>

                    )}
                </Translate>
            )
        }

const DetailModalContent = ({item}) => {
    const t = useTranslate()
    const {isSm} = useContext(LayoutContext)
    const {
        title,
        course_class_idcode,
        to_date,
        from_date,
        description,
        coordinators = [],
        class_teachers = [],
        number_of_students,
    } = item ?? {}

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 custom-border-primary rounded-md relative">
                <Avatar
                    size={60}
                    icon={<ClassroomSvg size={25} className="text-primary-400"/>}
                    className="flex flex-center bg-primary-50 rounded-l-md rounded-r-0"
                />
                <div className="flex flex-col gap-1">
                    <Link
                        to={getLinkToDetail(item)}
                        style={{fontSize: isSm ? 14 : 16}}
                        className="font-bold text-primary-600 leading-snug no-underline">
                        {getTitle(item)}
                    </Link>
                    <ItemInformation
                        label="course class idcode"
                        value={`#${course_class_idcode}`}
                        className="text-xs italic"
                    />
                </div>
            </div>
            {renderIf(
                description,
                <div className="text-sm text-color-500 italic">{description}</div>
            )}
            <div className="flex flex-col gap-2">
                <HeaderWithIconLine
                    icon={<BsInfoCircleFill size={14} className="text-color-500 mr-1"/>}
                    title="class information"
                    className="text-sm text-color-400"
                />
                <div className="ml-0 md:ml-1 flex flex-col gap-1">
                    {renderIf(
                        coordinators.length > 0,
                        <div className="space-x-1 text-xs italic">
                            <GoDotFill size={10} className="text-color-400"/>
                            <span className="text-color-500">
                                {`${t('class coordinator')} : `}
                            </span>
                            {coordinators.map((e, i) => (
                                <span key={i} className="font-semibold text-color-100">
                                    {e?.teacher.prefix_title} {e?.teacher_name} {i < coordinators.length - 1 && ', '}
                                </span>
                            ))}
                        </div>
                    )}
                    {renderIf(
                        class_teachers?.length > 0,
                        <div className="space-x-1 text-xs italic">
                            <GoDotFill size={10} className="text-color-400"/>
                            <span className="text-color-500">
                                {`${t('teachers')} : `}
                            </span>
                            {class_teachers.map((e, i) => e?.teacher && (
                                <span key={i} className="font-semibold text-color-100">
                                    {e?.teacher?.prefix_title} {getTitle(e?.teacher)} {i < coordinators?.length - 1 && ', '}
                                </span>
                            ))}
                        </div>
                    )}
                    <ItemInformation
                        showIcon={true}
                        label="number of student"
                        value={`${number_of_students} ${t('students')}`}
                        className="text-xs italic"
                    />
                    {renderIf(
                        _.some([from_date, to_date]),
                        <div className="space-x-1 text-xs italic">
                            <GoDotFill size={10} className="text-color-400"/>
                            <span className="text-color-500">
                                {`${t('time')} : `}
                            </span>
                            <span className="font-semibold text-color-100">
                                {renderFromToTime(from_date, to_date)}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const useCourseClasses = ({course}) => {
    const t = useTranslate()

    const history = useHistory()

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })

    const {edit} = course ?? {}

    const course_id = getId(course)

    const onCancel = () => {
        setAction({})
    }

    const modal = useMemo(() => {
        const {id} = action?.value ?? {}
        let Content = NullAsDefault
        let Wrapper = ({children}) => children({item: {}})
        let params = {}
        let modal_params = {}

        switch (action?.type) {
            case CRUD.READ:
                modal_params = {
                    width: 600,
                    title: (
                        <div className="text-center font-bold text-color-000 uppercase">
                            {t('class quick view')}
                        </div>
                    ),
                    footer: undefined,
                    okText: t('go to class'),
                    cancelText: t('close'),
                    okButtonProps: {
                        type: 'primary',
                        className: 'button-rounded-lg no-border',
                    },
                    cancelButtonProps: {
                        className: 'button-rounded-lg no-border text-color-200',
                    },
                    onOk: () => {
                        history.push(getLinkToDetail(action?.value))
                    },
                }
                Content = DetailModalContent
                Wrapper = withProps({id})(FetchClassById)
                break
            default:
                break
        }
        return (
            <Modal
                footer={null}
                onCancel={onCancel}
                destroyOnHidden={true}
                open={action?.type}
                className="custom-modal"
                {...modal_params}>
                <Wrapper>
                    {({item, isLoading}) => {
                        if (!!isLoading) {
                            return <LoadingBlock/>
                        }
                        return (
                            <Content
                                {...params}
                                {...{item, onCancel, course_id}}
                            />
                        )
                    }}
                </Wrapper>
            </Modal>
        )
    }, [action, course_id])

    const renderActions = useCallback(
        (item) =>
            renderIf(
                edit,
                <div
                    onClick={() => {
                        setAction({type: CRUD.READ, value: item})
                    }}
                    className="font-normal text-xs text-color-400 hover:text-color-100 italic tracking-wide cursor-pointer">
                    {`${t('quick view')} ...`}
                </div>
            ),
        [edit]
    )

    return {
        modal,
        action,
        onCancel,
        setAction,
        course_id,
        renderActions,
    }
}

const ClassList = ({item, ...props}) => {
    const course_id = getId(item)

    const apiInfo = course_getCourseClasses_Api

    const refreshKey = useMemo(
        () => [apiInfo.path.replace(':id', course_id)].join('/'),
        [course_id]
    )

    const {modal, renderActions} = useCourseClasses({
        course: item,
    })

    return (
        <React.Fragment>
            <EntityList
                key={refreshKey}
                refreshKey={refreshKey}
                // values={{ admin_only: 1 }}
                RenderEntity={React.Fragment}
                renderEmpty={renderAdminOnlyHolder}
                apiInfo={course_getCourseClasses_Api}
                query={bindQueryParam({id: course_id})}
                renderItem={renderItem({renderActions})}
                renderWrap={(child, __, count) =>
                    renderIf(
                        count,
                        <div className="flex flex-col items-start space-y-6">
                            <div className="w-full flex flex-col flex-1 gap-4">
                                <HeaderWithIconLine
                                    icon={<ClassroomSvg size={18} className="text-color-500"/>}
                                    title="list of classes"
                                    className="text-lg text-color-400"
                                />
                                <div className="ml-4 flex flex-col gap-4">{child}</div>
                            </div>
                        </div>
                    )
                }
                {...props}
            />
            {modal}
        </React.Fragment>
    )
}

const CourseClassTab = (props) => {
    const {item} = useContext(CourseContext)
    return (
        <ClassList
            item={item}
            {...props}
        />
    )
}

export const CourseClassShortList = (props) => {
    const {item} = useContext(CourseContext)
    return (
        <ClassList
            item={item}
            sample={2}
            {...props}
        />
    )
}

export const CourseClassesInfo = ({course}) => {
    const course_classes = Array.from(course?.course_classes ?? [])
    const {modal, renderActions} = useCourseClasses({
        course,
    })
    return (
        <div className="flex flex-col items-start space-y-6">
            <div className="w-full flex flex-col flex-1 gap-4">
                <ListHeaderWithLine title="list of classes"/>
                <div className="flex flex-col gap-3">
                    {course_classes.length > 0 ? (
                        course_classes.map(
                            renderItem({
                                renderActions,
                            })
                        )
                    ) : (
                        <EmptyHolder/>
                    )}
                </div>
            </div>
            {modal}
        </div>
    )
}

export default CourseClassTab
